import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { trackEvent } from '../utils/tracking';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      setIsScrolled(currentScrollY > 50);
      
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const navItems = [
    { label: t.nav.about, href: '#sobre' },
    { label: t.nav.services, href: '#servicos' },
    { label: t.nav.clients, href: '#clientes' },
    { label: t.nav.form, href: '/form', isPage: true },
    { label: t.nav.contact, href: '#contacto' },
  ];

  const handleNavClick = (item) => {
    setIsMobileMenuOpen(false);
    if (item.isPage) {
      navigate(item.href);
    } else {
      const element = document.querySelector(item.href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      <motion.header
        data-testid="header"
        initial={{ y: 0 }}
        animate={{ y: isVisible ? 0 : -100 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
          isScrolled ? 'glass-header border-b border-[#242328]/5' : 'bg-transparent'
        }`}
      >
        <div className="container-main">
          <nav className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <Link 
              to="/"
              data-testid="logo"
              className="relative z-50"
            >
              <img 
                src="/logo-loom.png" 
                alt="Loom Studio" 
                className="h-20 md:h-24 w-auto"
                style={{ filter: 'invert(1) brightness(0.15)' }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6 lg:gap-10">
              {navItems.map((item) => (
                item.isPage ? (
                  <Link
                    key={item.href}
                    to={item.href}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className="animated-underline text-sm font-medium text-[#242328]/80 hover:text-[#242328] transition-colors duration-200"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.href}
                    onClick={() => handleNavClick(item)}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className="animated-underline text-sm font-medium text-[#242328]/80 hover:text-[#242328] transition-colors duration-200"
                  >
                    {item.label}
                  </button>
                )
              ))}
              
              {/* Language Switcher */}
              <LanguageSwitcher variant="light" />
              
              <Link
                to="/form"
                data-testid="header-cta"
                onClick={() => trackEvent('cta_click')}
                className="btn-primary text-sm py-3 px-6"
              >
                {t.nav.bookMeeting}
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-3">
              <LanguageSwitcher variant="light" />
              <button
                data-testid="mobile-menu-toggle"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="relative z-50 p-2"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6 text-[#242328]" strokeWidth={1.5} />
                ) : (
                  <Menu className="w-6 h-6 text-[#242328]" strokeWidth={1.5} />
                )}
              </button>
            </div>
          </nav>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-30 bg-[#f8f6e2] md:hidden"
          >
            <div className="flex flex-col items-center justify-center h-full gap-8">
              {navItems.map((item, index) => (
                item.isPage ? (
                  <motion.div
                    key={item.href}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      className="font-['Syne'] text-3xl font-semibold text-[#242328] hover:text-[#ff00c7] transition-colors duration-200"
                    >
                      {item.label}
                    </Link>
                  </motion.div>
                ) : (
                  <motion.button
                    key={item.href}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => handleNavClick(item)}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className="font-['Syne'] text-3xl font-semibold text-[#242328] hover:text-[#ff00c7] transition-colors duration-200"
                  >
                    {item.label}
                  </motion.button>
                )
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Link
                  to="/form"
                  onClick={() => { setIsMobileMenuOpen(false); trackEvent('cta_click'); }}
                  data-testid="mobile-cta"
                  className="btn-primary mt-4"
                >
                  {t.nav.bookMeeting}
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
